import { GraphFilters, NotificationActionFilters } from '../../models/filters';
import { FilteringConfig } from '../../models/grid';
import { TimeRange } from '../../models/time.model';

const arraysEqual = (arr1: number[], arr2: number[]): boolean => {
  if (arr1.length !== arr2.length) {
    return false;
  }

  const sorted1 = [...arr1].sort();
  const sorted2 = [...arr2].sort();

  return sorted1.every((val, idx) => val === sorted2[idx]);
};

export const areGraphFiltersEqual = (
  currentFilters: GraphFilters,
  backendFilters: GraphFilters,
): boolean => {
  if (
    !arraysEqual(currentFilters.companies || [], backendFilters.companies || [])
  ) {
    return false;
  }

  if (
    !arraysEqual(currentFilters.services || [], backendFilters.services || [])
  ) {
    return false;
  }

  if (!arraysEqual(currentFilters.sites || [], backendFilters.sites || [])) {
    return false;
  }

  if (
    !arraysEqual(currentFilters.statuses || [], backendFilters.statuses || [])
  ) {
    return false;
  }

  const date1Type = currentFilters.dateRange?.type || '';
  const date2Type = backendFilters.dateRange?.type || '';

  if (date1Type !== date2Type) {
    return false;
  }

  if (date1Type === TimeRange.Custom) {
    const startDate = currentFilters.dateRange?.customRange || [];
    const endDate = backendFilters.dateRange?.customRange || [];

    if (startDate.length !== endDate.length) {
      return false;
    }

    return startDate.every((date1: Date | string, idx: number) => {
      const date2 = endDate[idx];

      const time1 =
        typeof date1 === 'string'
          ? new Date(date1).getTime()
          : date1?.getTime();
      const time2 =
        typeof date2 === 'string'
          ? new Date(date2).getTime()
          : date2?.getTime();

      return time1 === time2;
    });
  }

  return true;
};

export const extractActiveFilters = (
  filtering: FilteringConfig,
): FilteringConfig =>
  Object.fromEntries(
    Object.entries(filtering).filter(([_, activeItem]: [string, any]) => {
      if (Array.isArray(activeItem.value)) {
        return activeItem.value.length > 0;
      }

      return (
        activeItem.value !== null &&
        activeItem.value !== undefined &&
        activeItem.value !== ''
      );
    }),
  );

export const areFilteringConfigsEqual = (
  config1: FilteringConfig,
  config2: FilteringConfig,
): boolean => {
  const keys1 = Object.keys(config1 || {});
  const keys2 = Object.keys(config2 || {});

  if (keys1.length !== keys2.length) {
    return false;
  }

  return keys1.every((key) => {
    const filter1 = config1[key];
    const filter2 = config2[key];

    if (!filter2) return false;

    return (
      JSON.stringify(filter1.value) === JSON.stringify(filter2.value) &&
      filter1.matchMode === filter2.matchMode &&
      filter1.operator === filter2.operator
    );
  });
};

export const areNotificationActionFiltersEqual = (
  currentFilters: NotificationActionFilters,
  backendFilters: NotificationActionFilters,
): boolean => {
  if (
    !arraysEqual(currentFilters.companies || [], backendFilters.companies || [])
  ) {
    return false;
  }

  if (
    !arraysEqual(currentFilters.services || [], backendFilters.services || [])
  ) {
    return false;
  }

  if (!arraysEqual(currentFilters.sites || [], backendFilters.sites || [])) {
    return false;
  }

  const cats1 = [...(currentFilters.categories || [])].sort();
  const cats2 = [...(backendFilters.categories || [])].sort();

  if (cats1.length !== cats2.length) {
    return false;
  }

  return cats1.every((val, idx) => val === cats2[idx]);
};
